import { useState, useEffect } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

async function fetchInstructors() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructors?select=*&order=name.asc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );
  return response.json();
}

async function fetchInstitutions() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/institutions?select=id,name&order=name.asc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    throw new Error(`Loading institutions failed (${response.status})`);
  }

  return response.json();
}

async function createInstitution(name) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/institutions`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({ name })
    }
  );

  if (!response.ok) {
    throw new Error(`Creating institution failed (${response.status})`);
  }

  const result = await response.json();

  if (!result[0]?.id) {
    throw new Error('Institution was not created.');
  }

  return result[0];
}

function normalizeInstitutionName(value) {
  return String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

async function fetchCourses() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/courses?select=id,name,instructor_id,course_title_id,semester_id,course_titles(name),semesters(name)&order=created_at.desc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );
  return response.json();
}

async function fetchCourseAccess() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_access?select=*`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );
  return response.json();
}

async function saveInstructor(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructors`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );
  return response.json();
}

async function updateInstructor(id, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructors?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    throw new Error(`Updating instructor failed (${response.status})`);
  }

  const result = await response.json();

  if (!result[0]) {
    throw new Error('Instructor was not updated.');
  }

  return result[0];
}

async function saveCourseAccess(instructorId, courseId) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/course_access`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ instructor_id: instructorId, course_id: courseId })
    }
  );
}

async function deleteCourseAccess(instructorId, courseId) {
  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };

  const unassignResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?course_id=eq.${courseId}&assigned_instructor_id=eq.${instructorId}`,
    {
      method: 'PATCH',
      headers: {
        ...headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ assigned_instructor_id: null })
    }
  );

  if (!unassignResponse.ok) {
    throw new Error(`Clearing instructor assignments failed (${unassignResponse.status})`);
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_access?instructor_id=eq.${instructorId}&course_id=eq.${courseId}`,
    {
      method: 'DELETE',
      headers
    }
  );

  if (!response.ok) {
    throw new Error(`Removing course access failed (${response.status})`);
  }
}

async function deleteInstructor(id) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/instructors?id=eq.${id}`,
    {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );
}

function courseName(course) {
  return (course.course_titles?.name || course.name || 'Untitled') + ' — ' + (course.semesters?.name || '');
}

export default function AccessControl({ session }) {
  const [instructors, setInstructors] = useState([]);
  const [courses, setCourses] = useState([]);
  const [access, setAccess] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newInstitution, setNewInstitution] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editInstitution, setEditInstitution] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
  Promise.all([
    fetchInstructors(),
    fetchCourses(),
    fetchCourseAccess(),
    fetchInstitutions()
  ])
    .then(([i, c, a, institutionRows]) => {
      setInstructors(i);
      setCourses(c);
      setAccess(a);
      setInstitutions(
        Array.isArray(institutionRows)
          ? institutionRows
          : []
      );
    })
    .catch(err => {
      console.error('Access control load failed:', err);
      setError('Failed to load access-control information.');
    })
    .finally(() => setLoading(false));
}, []);

async function resolveInstitution(name) {
  const cleanName = String(name || '')
    .trim()
    .replace(/\s+/g, ' ');

  if (!cleanName) {
    return null;
  }

  const existing = institutions.find(
    institution =>
      normalizeInstitutionName(institution.name) ===
      normalizeInstitutionName(cleanName)
  );

  if (existing) {
    return existing;
  }

  const created = await createInstitution(cleanName);

  setInstitutions(prev =>
    [...prev, created].sort((a, b) =>
      a.name.localeCompare(b.name)
    )
  );

  return created;
}

function getInstitutionName(instructor) {
  return (
    institutions.find(
      institution =>
        institution.id === instructor.institution_id
    )?.name || ''
  );
}

  async function handleAddInstructor(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!newName.trim()) return setError('Enter a name.');
    if (!newEmail.trim()) return setError('Enter an email address.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim())) return setError('Enter a valid email address.');

    const duplicate = instructors.find(
      i => i.email.toLowerCase() === newEmail.trim().toLowerCase()
    );

    if (duplicate) return setError('That email address already has an account.');

    setSaving(true);
    try {
      const institution = await resolveInstitution(
  newInstitution
);

const result = await saveInstructor({
  name: newName.trim(),
  email: newEmail.trim().toLowerCase(),
  institution_id: institution?.id || null,
  is_superadmin: false
});

      setInstructors(prev =>
        [...prev, result[0]].sort((a, b) => a.name.localeCompare(b.name))
      );

      setNewName('');
      setNewEmail('');
      setNewInstitution('');
      setSuccess('Instructor added.');
    } catch (err) {
      setError('Failed to add instructor.');
    } finally {
      setSaving(false);
    }
  }

  function handleStartEdit(instructor) {
    setError('');
    setSuccess('');
    setEditingId(instructor.id);
    setEditName(instructor.name || '');
    setEditEmail(instructor.email || '');
    setEditInstitution(
  getInstitutionName(instructor)
);
  }

  function handleCancelEdit() {
    setEditingId(null);
    setEditName('');
    setEditEmail('');
    setEditInstitution('');
  }

  async function handleSaveEdit(e, instructor) {
    e.preventDefault();
    setError('');
    setSuccess('');

    const name = editName.trim();
    const email = editEmail.trim().toLowerCase();

    if (!name) return setError('Enter a name.');
    if (!email) return setError('Enter an email address.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return setError('Enter a valid email address.');
    }

    const duplicate = instructors.find(
      i =>
        i.id !== instructor.id &&
        i.email.toLowerCase() === email
    );

    if (duplicate) {
      return setError('That email address already has an account.');
    }

    setEditSaving(true);

    try {
      const institution = await resolveInstitution(
  editInstitution
);
const updated = await updateInstructor(instructor.id, {
  name,
  email,
  institution_id: institution?.id || null
});

      setInstructors(prev =>
        prev
          .map(i => i.id === instructor.id ? updated : i)
          .sort((a, b) => a.name.localeCompare(b.name))
      );

      // If the logged-in superadmin edits their own name/email,
      // keep the saved browser session in sync for the next reload.
      if (session?.instructorId === instructor.id) {
        try {
          const stored = JSON.parse(localStorage.getItem('cmsSession'));

          if (stored) {
            localStorage.setItem(
              'cmsSession',
              JSON.stringify({
                ...stored,
                name: updated.name,
                instructorName: updated.name,
                email: updated.email
              })
            );
          }
        } catch (err) {
          console.error('Could not update saved CMS session:', err);
        }
      }

      setEditingId(null);
      setEditName('');
      setEditEmail('');
      setEditInstitution('');
      setSuccess('Instructor information updated.');
    } catch (err) {
      setError(err.message || 'Failed to update instructor.');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleToggleAccess(instructorId, courseId, currentlyGranted) {
    if (currentlyGranted) {
      await deleteCourseAccess(instructorId, courseId);
      setAccess(prev =>
        prev.filter(a => !(a.instructor_id === instructorId && a.course_id === courseId))
      );
    } else {
      await saveCourseAccess(instructorId, courseId);
      setAccess(prev => [...prev, { instructor_id: instructorId, course_id: courseId }]);
    }
  }

  async function handleDeleteInstructor(instructor) {
    if (!window.confirm(`Remove access for ${instructor.name}? This cannot be undone.`)) return;

    await deleteInstructor(instructor.id);
    setInstructors(prev => prev.filter(i => i.id !== instructor.id));
    setAccess(prev => prev.filter(a => a.instructor_id !== instructor.id));

    if (editingId === instructor.id) {
      handleCancelEdit();
    }

    setSuccess('Instructor removed.');
  }

  function editForm(instructor) {
    return (
      <form onSubmit={e => handleSaveEdit(e, instructor)} style={styles.editForm}>
        <div style={styles.editField}>
          <label style={styles.label}>Name</label>
          <input
            style={styles.input}
            type="text"
            value={editName}
            onChange={e => setEditName(e.target.value)}
            disabled={editSaving}
          />
        </div>

        <div style={styles.editField}>
          <label style={styles.label}>Email</label>
          <input
            style={styles.input}
            type="email"
            value={editEmail}
            onChange={e => setEditEmail(e.target.value)}
            disabled={editSaving}
          />
        </div>

<div style={styles.editField}>
  <label style={styles.label}>Institution</label>
  <input
    style={styles.input}
    type="text"
    list="institution-options"
    value={editInstitution}
    onChange={e => setEditInstitution(e.target.value)}
    placeholder="Select or enter institution"
    disabled={editSaving}
  />
</div>

        <div style={styles.editActions}>
          <button style={styles.btn} disabled={editSaving}>
            {editSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            style={styles.secondaryBtn}
            onClick={handleCancelEdit}
            disabled={editSaving}
          >
            Cancel
          </button>
        </div>
      </form>
    );
  }

  const nonSuperadmins = instructors.filter(i => !i.is_superadmin);
  const superadmins = instructors.filter(i => i.is_superadmin);

  if (loading) return <div style={styles.muted}>Loading...</div>;

  return (
  <div>
    <datalist id="institution-options">
      {institutions.map(institution => (
        <option
          key={institution.id}
          value={institution.name}
        />
      ))}
    </datalist>

    <h2 style={styles.subheading}>Access Control</h2>
      <p style={styles.muted}>
        Instructor accounts are created here. Course owners always retain full access to
        courses they created; the checkboxes below grant or remove secondary grading access.
      </p>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <div style={styles.section}>
        <h3 style={styles.sectionHeading}>Superadmins</h3>

        {superadmins.map(instructor => (
          <div key={instructor.id} style={styles.instructorRow}>
            {editingId === instructor.id ? (
              editForm(instructor)
            ) : (
              <>
                <div>
                  <div style={styles.instructorName}>{instructor.name}</div>
<div style={styles.instructorEmail}>{instructor.email}</div>
{getInstitutionName(instructor) && (
  <div style={styles.instructorInstitution}>
    {getInstitutionName(instructor)}
  </div>
)}
                </div>

                <div style={styles.actions}>
                  <button
                    type="button"
                    style={styles.secondaryBtn}
                    onClick={() => handleStartEdit(instructor)}
                  >
                    Edit
                  </button>
                  <span style={styles.superadminPill}>Superadmin</span>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionHeading}>Other Instructors</h3>

        {nonSuperadmins.length === 0 && (
          <div style={styles.muted}>No other instructors added yet.</div>
        )}

        {nonSuperadmins.map(instructor => {
          const grantedCourseIds = access
            .filter(a => a.instructor_id === instructor.id)
            .map(a => a.course_id);

          return (
            <div key={instructor.id} style={styles.instructorCard}>
              <div style={styles.instructorCardHeader}>
                {editingId === instructor.id ? (
                  editForm(instructor)
                ) : (
                  <>
                    <div>
                      <div style={styles.instructorName}>{instructor.name}</div>
<div style={styles.instructorEmail}>{instructor.email}</div>
{getInstitutionName(instructor) && (
  <div style={styles.instructorInstitution}>
    {getInstitutionName(instructor)}
  </div>
)}
                    </div>

                    <div style={styles.actions}>
                      <button
                        type="button"
                        style={styles.secondaryBtn}
                        onClick={() => handleStartEdit(instructor)}
                      >
                        Edit
                      </button>
                      <button
                        style={styles.dangerBtn}
                        onClick={() => handleDeleteInstructor(instructor)}
                      >
                        Remove
                      </button>
                    </div>
                  </>
                )}
              </div>

              <div style={styles.courseAccessList}>
                <div style={styles.courseAccessLabel}>Course access:</div>

                {courses.length === 0 && (
                  <div style={styles.muted}>No courses created yet.</div>
                )}

                {courses.map(course => {
                  const isOwner = course.instructor_id === instructor.id;
                  const granted = grantedCourseIds.includes(course.id);

                  return (
                    <label
                      key={course.id}
                      style={{
                        ...styles.courseCheckRow,
                        ...(isOwner ? styles.ownerCourseRow : {})
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isOwner || granted}
                        disabled={isOwner}
                        onChange={() =>
                          handleToggleAccess(instructor.id, course.id, granted)
                        }
                        style={{ marginRight: '8px' }}
                      />
                      {courseName(course)}
                      {isOwner && <span style={styles.ownerPill}>Owner</span>}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}

        <form onSubmit={handleAddInstructor} style={styles.addForm}>
          <h4 style={styles.addHeading}>Add Instructor</h4>
          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>Name</label>
              <input
                style={styles.input}
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Full name"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input
                style={styles.input}
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                placeholder="email@example.com"
              />
            </div>

            <div style={styles.fieldBtn}>
              <label style={styles.label}>&nbsp;</label>
              <button style={styles.btn} disabled={saving}>
                {saving ? 'Adding...' : 'Add Instructor'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  subheading: { margin: '0 0 8px', fontSize: '18px', color: '#1f2933' },
  muted: { color: '#66788a', fontSize: '14px', margin: '8px 0' },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '8px',
    padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px'
  },
  success: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '8px',
    padding: '10px 14px', color: '#216e45', fontSize: '14px', marginBottom: '16px'
  },
  section: { marginBottom: '32px' },
  sectionHeading: { fontSize: '15px', color: '#1f2933', margin: '0 0 12px' },
  instructorRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 14px', border: '1px solid #d7dee5', borderRadius: '8px',
    background: 'white', marginBottom: '8px'
  },
  instructorCard: {
    border: '1px solid #d7dee5', borderRadius: '10px',
    background: 'white', marginBottom: '12px', overflow: 'hidden'
  },
  instructorCardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 14px', borderBottom: '1px solid #d7dee5'
  },
  instructorName: { fontWeight: '700', fontSize: '14px', color: '#1f2933' },
  instructorEmail: { fontSize: '13px', color: '#66788a', marginTop: '2px' },
   instructorInstitution: {
  fontSize: '13px',
  color: '#66788a',
  marginTop: '2px',
  fontStyle: 'italic'
},
  actions: { display: 'flex', alignItems: 'center', gap: '8px' },
  superadminPill: {
    background: '#e7f1f7', color: '#215b7d', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
   courseAccessList: { padding: '12px 14px' },
  courseAccessLabel: {
    fontSize: '12px', fontWeight: '700', color: '#66788a', marginBottom: '8px',
    textTransform: 'uppercase', letterSpacing: '0.05em'
  },
  courseCheckRow: {
    display: 'flex', alignItems: 'center', fontSize: '14px',
    color: '#1f2933', marginBottom: '6px', cursor: 'pointer', gap: '6px'
  },
  ownerCourseRow: {
    color: '#66788a',
    cursor: 'default'
  },
  ownerPill: {
    marginLeft: '4px',
    padding: '2px 7px',
    borderRadius: '999px',
    background: '#eef2f5',
    color: '#66788a',
    fontSize: '11px',
    fontWeight: '700'
  },
  editForm: {
    display: 'flex', alignItems: 'flex-end', gap: '12px',
    flexWrap: 'wrap', width: '100%'
  },
  editField: { flex: 1, minWidth: '180px' },
  editActions: { display: 'flex', gap: '8px' },
  addForm: {
    border: '1px solid #d7dee5', borderRadius: '10px', padding: '16px',
    background: '#f7f9fa', marginTop: '16px'
  },
  addHeading: {
    margin: '0 0 14px', fontSize: '14px',
    color: '#1f2933', fontWeight: '700'
  },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  field: { flex: 1, minWidth: '180px' },
  fieldBtn: { flexShrink: 0 },
  label: {
    display: 'block', fontWeight: '700', fontSize: '13px',
    marginBottom: '6px', color: '#1f2933'
  },
  input: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '9px 11px', fontSize: '14px', boxSizing: 'border-box', background: 'white'
  },
  btn: {
    background: '#215b7d', color: 'white', border: 'none', borderRadius: '8px',
    padding: '9px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
  },
  secondaryBtn: {
    background: 'transparent', color: '#215b7d', border: '1px solid #b9cad4',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer'
  },
  dangerBtn: {
    background: 'transparent', color: '#a12a2a', border: '1px solid #e2b8b8',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer'
  }
};